import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import TopBar, { iconBtn } from '../../components/ui/TopBar';
import TagChip from '../../components/ui/TagChip';
import ActionTile from '../../components/ui/ActionTile';
import CommentItem from '../../components/ui/CommentItem';
import VideoGrid from '../../components/ui/VideoGrid';
import VideoCard from '../../components/ui/VideoCard';
import VideoPlayer from '../../components/ui/VideoPlayer';
import Icon from '../../components/ui/Icon';
import { API } from '../../utils/api';
import { buildPosterUrl, fmtViews, fmtDate, fmtNum } from '../../utils/format';
import { getFingerprint } from '../../utils/fingerprint';
import { slugify } from '../../utils/slugify';
import { addToHistory, updateHistoryProgress, isSaved, toggleSaved, isLiked, toggleLike } from '../../utils/storage';

/* ── Helpers ─────────────────────────────────────────────────── */
const Dot = () => (
    <span style={{ width: 3, height: 3, borderRadius: 3, background: '#3F3F46', display: 'inline-block', margin: '0 2px' }} />
);

const getWatchBootstrap = (videoId) => {
    if (typeof window === 'undefined') return null;
    const data = window.__WATCH_PAGE_DATA__;
    return data && String(data.video_id) === String(videoId) ? data : null;
};

/* ── Reactions block ─────────────────────────────────────────── */
const REACTIONS = [
    { key: 'fire',    emoji: '🔥', label: 'Огонь' },
    { key: 'heart',   emoji: '❤️', label: 'Любимое' },
    { key: 'eye',     emoji: '👀', label: 'Ого' },
    { key: 'sparkle', emoji: '✨', label: 'Шедевр' },
];

function ReactionsBlock({ videoId }) {
    const [counts, setCounts] = useState(() => ({
        fire: 0, heart: 0, eye: 0, sparkle: 0,
        ...(getWatchBootstrap(videoId)?.reactions ?? {}),
    }));
    const [mine,   setMine]   = useState([]);

    useEffect(() => {
        const fp = getFingerprint();
        fetch(`${API}/reactions.php?video_id=${videoId}&fp=${fp}`)
            .then(r => r.json())
            .then(data => {
                if (data.counts) setCounts(data.counts);
                if (data.mine)   setMine(data.mine);
            })
            .catch(() => {});
    }, [videoId]);

    const toggle = async (type) => {
        const fp   = getFingerprint();
        const isOn = mine.includes(type);

        setMine(prev => isOn ? prev.filter(t => t !== type) : [...prev, type]);
        setCounts(prev => ({
            ...prev,
            [type]: Math.max(0, (prev[type] || 0) + (isOn ? -1 : 1)),
        }));

        try {
            const res  = await fetch(`${API}/reactions.php?video_id=${videoId}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ reaction_type: type, fingerprint: fp }),
            });
            const data = await res.json();
            if (data.counts) setCounts(data.counts);
        } catch {}
    };

    const total = Object.values(counts).reduce((a, b) => a + b, 0);

    return (
        <div style={{
            padding: '14px 14px',
            background: '#0E0E10', border: '1px solid #1A1A1D', borderRadius: 14,
            marginTop: 18,
        }}>
            <div style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 10, color: '#71717A',
                letterSpacing: 1.2, textTransform: 'uppercase', marginBottom: 10,
            }}>Реакции · {fmtNum(total)}</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {REACTIONS.map(({ key, emoji, label }) => {
                    const on = mine.includes(key);
                    return (
                        <button key={key} onClick={() => toggle(key)} style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            padding: '6px 12px',
                            background: on ? 'rgba(230,0,115,0.13)' : '#17171A',
                            border: `1px solid ${on ? '#e60073' : '#27272A'}`,
                            borderRadius: 999, fontSize: 12, fontWeight: 500, cursor: 'pointer',
                        }}>
                            <span style={{ fontSize: 14 }}>{emoji}</span>
                            <span style={{
                                fontFamily: "'JetBrains Mono', monospace",
                                fontSize: 11, color: on ? '#e60073' : '#A1A1AA',
                            }}>{fmtNum(counts[key] || 0)}</span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

/* ── Comment composer ────────────────────────────────────────── */
function CommentComposer({ videoId, onPosted }) {
    const [draft, setDraft] = useState('');
    const [posting, setPosting] = useState(false);

    const submit = async () => {
        const text = draft.trim();
        if (!text) return;
        setPosting(true);
        try {
            await fetch(`${API}/postComment.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ video_id: videoId, nickname: 'Гость', content: text }),
            });
            setDraft('');
            onPosted?.();
        } catch {}
        setPosting(false);
    };

    return (
        <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
            <div style={{
                width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                background: 'linear-gradient(135deg, #e60073, #831843)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontSize: 13, fontWeight: 700,
            }}>Г</div>
            <div style={{ flex: 1 }}>
                <textarea
                    value={draft}
                    onChange={e => setDraft(e.target.value)}
                    placeholder="Поделитесь впечатлениями…"
                    style={{
                        width: '100%', minHeight: 60, resize: 'none',
                        background: '#0E0E10', border: '1px solid #1A1A1D',
                        borderRadius: 12, color: '#fff', fontSize: 13,
                        padding: 12, outline: 'none', boxSizing: 'border-box',
                        fontFamily: "'Manrope', sans-serif",
                    }}
                />
                <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                        onClick={submit}
                        disabled={!draft.trim() || posting}
                        style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            padding: '8px 14px',
                            background: draft.trim() ? '#e60073' : '#1F1F23',
                            border: 0, borderRadius: 10,
                            color: '#fff', fontSize: 12, fontWeight: 600,
                            cursor: draft.trim() ? 'pointer' : 'not-allowed',
                            opacity: posting ? 0.6 : 1,
                            fontFamily: "'Manrope', sans-serif",
                        }}
                    >
                        <Icon name="send" size={14} color="#fff" stroke={2} />
                        Отправить
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ── Main component ──────────────────────────────────────────── */
/* Студия и продюсеры — выводятся только у тех видео, где заполнены, кликабельны на /studio/:slug и /producer/:slug */
function MetaFactLink({ to, children }) {
    const navigate = useNavigate();
    return (
        <a
            href={to}
            onClick={(e) => { e.preventDefault(); navigate(to); }}
            style={{ color: '#E4E4E7', textDecoration: 'none' }}
            onMouseEnter={(e) => { e.currentTarget.style.color = '#e60073'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = '#E4E4E7'; }}
        >
            {children}
        </a>
    );
}

function MetaFacts({ studio, producers }) {
    const studioName    = String(studio || '').trim();
    const producerNames = String(producers || '')
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);

    if (studioName === '' && producerNames.length === 0) return null;

    return (
        <div style={{
            marginTop: 14, display: 'flex', flexDirection: 'column', gap: 8,
            padding: '12px 14px',
            background: '#0E0E11', border: '1px solid #1F1F23', borderRadius: 12,
        }}>
            {studioName !== '' && (
                <div style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}>
                    <span style={{
                        fontFamily: "'JetBrains Mono', monospace",
                        fontSize: 10, letterSpacing: 1.1, textTransform: 'uppercase',
                        color: '#71717A', flexShrink: 0, minWidth: 86,
                    }}>Студия</span>
                    <span style={{ fontSize: 13, lineHeight: 1.5 }}>
                        <MetaFactLink to={`/studio/${encodeURIComponent(slugify(studioName))}`}>{studioName}</MetaFactLink>
                    </span>
                </div>
            )}
            {producerNames.length > 0 && (
                <div style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}>
                    <span style={{
                        fontFamily: "'JetBrains Mono', monospace",
                        fontSize: 10, letterSpacing: 1.1, textTransform: 'uppercase',
                        color: '#71717A', flexShrink: 0, minWidth: 86,
                    }}>Продюсеры</span>
                    <span style={{ fontSize: 13, lineHeight: 1.5 }}>
                        {producerNames.map((name, i) => (
                            <React.Fragment key={name}>
                                {i > 0 && ', '}
                                <MetaFactLink to={`/producer/${encodeURIComponent(slugify(name))}`}>{name}</MetaFactLink>
                            </React.Fragment>
                        ))}
                    </span>
                </div>
            )}
        </div>
    );
}

export default function WatchPage() {
    const { id }     = useParams();
    const navigate   = useNavigate();
    const videoRef   = useRef(null);
    const progressTimer = useRef(null);
    const initialWatchData = getWatchBootstrap(id);

    const [video,    setVideo]    = useState(() => {
        if (typeof window !== 'undefined') {
            const d = window.__VIDEO_DATA__;
            return d && String(d.id) === String(id) ? d : null;
        }
        return null;
    });
    const [comments,      setComments]      = useState(() => initialWatchData?.comments ?? []);
    const [related,       setRelated]       = useState(() => initialWatchData?.related ?? []);
    const [series,        setSeries]        = useState(() => initialWatchData?.series ?? []);
    const [seriesTotal,   setSeriesTotal]   = useState(() => initialWatchData?.series_total ?? 0);
    const [seriesLoading, setSeriesLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('comments');
    const [liked,     setLiked]     = useState(false);
    const [saved,     setSaved]     = useState(false);
    const [showDesc,  setShowDesc]  = useState(false);
    const [commKey,   setCommKey]   = useState(0);

    const posterUrl = buildPosterUrl(video?.poster_url);

    /* Load video data */
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        const boot = typeof window !== 'undefined' && window.__VIDEO_DATA__;
        if (boot && String(boot.id) === String(id)) {
            setVideo(boot);
            return;
        }
        setVideo(null);
        fetch(`${API}/video.php?id=${id}`)
            .then(r => r.json())
            .then(data => { if (!data.error) setVideo(data); });
    }, [id]);

    /* After video loaded */
    useEffect(() => {
        if (!video) return;
        setLiked(isLiked(video.id));
        setSaved(isSaved(video.id));
        addToHistory(video);

        fetch(`${API}/comments.php?video_id=${id}`)
            .then(r => r.json()).then(setComments).catch(() => {});

        fetch(`${API}/related_videos.php?id=${id}`)
            .then(r => r.json()).then(d => setRelated(Array.isArray(d) ? d : d.results ?? [])).catch(() => {});

        if (video.series) {
            fetch(`${API}/series_videos.php?series_id=${video.series}&current_id=${id}`)
                .then(r => r.json())
                .then(d => { setSeries(d.results ?? []); setSeriesTotal(d.total ?? 0); })
                .catch(() => {});
        } else {
            setSeries([]);
            setSeriesTotal(0);
        }
    }, [video, id]);

    /* Progress tracking */
    useEffect(() => {
        const el = videoRef.current;
        if (!el || !video) return;

        const save = () => {
            const dur = el.duration;
            if (!dur || isNaN(dur)) return;
            updateHistoryProgress(video.id, el.currentTime / dur);
        };

        progressTimer.current = setInterval(save, 10000);
        el.addEventListener('pause', save);
        el.addEventListener('ended', save);

        return () => {
            clearInterval(progressTimer.current);
            el.removeEventListener('pause', save);
            el.removeEventListener('ended', save);
            save();
        };
    }, [video]);

    const handleLike = () => {
        const now = toggleLike(video.id);
        setLiked(now);
        // Fire-and-forget server-side like
        fetch(`${API}/like.php`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ video_id: video.id, action: now ? 'like' : 'unlike' }),
        }).catch(() => {});
    };

    const handleSave = () => {
        const now = toggleSaved(video.id);
        setSaved(now);
    };

    const loadAllSeries = async () => {
        setSeriesLoading(true);
        try {
            const res  = await fetch(`${API}/series_videos.php?series_id=${video.series}&current_id=${id}&all=1`);
            const data = await res.json();
            setSeries(data.results ?? []);
            setSeriesTotal(data.total ?? 0);
        } catch {}
        setSeriesLoading(false);
    };

    const handleShare = () => {
        if (navigator.share) {
            navigator.share({ title: video.title, url: window.location.href }).catch(() => {});
        } else {
            navigator.clipboard.writeText(window.location.href).catch(() => {});
        }
    };

    if (!video) return (
        <div style={{ minHeight: '100vh', background: '#0A0A0B' }}>
            <TopBar left={
                <a href="/" onClick={(e) => { e.preventDefault(); navigate('/'); }} style={{ textDecoration: 'none' }}>
                    <span style={{ fontSize: 17, fontWeight: 800, color: '#e60073', letterSpacing: -0.5 }}>
                        Hentai<span style={{ color: '#fff' }}>Baza</span>
                    </span>
                </a>
            } />
            <div className="hb-spinner" style={{ marginTop: 60 }} />
        </div>
    );

    if (video.error) return (
        <div style={{ padding: '2rem', color: '#e60073' }}>Видео не найдено</div>
    );

    const canonicalUrl = `https://hentaibaza.com/watch/${id}`;
    const metaDescription = video.description || `Смотрите ${video.title} на HentaiBaza`;
    const tabs = [
        { key: 'comments', label: `Комментарии · ${comments.length}` },
    ];

    return (
        <>
            <Helmet>
                <title>{video.title} · HentaiBaza</title>
                <meta name="description" content={metaDescription} />
                <meta name="robots" content="index, follow" />
                <link rel="canonical" href={canonicalUrl} />
                <meta property="og:title"       content={`${video.title} · HentaiBaza`} />
                <meta property="og:description" content={metaDescription} />
                <meta property="og:url"         content={canonicalUrl} />
                {posterUrl && <meta property="og:image" content={posterUrl} />}
                <meta property="og:type"        content="video.other" />
                {video.file_url && <meta property="og:video:url" content={video.file_url} />}
                {video.file_url && <meta property="og:video:secure_url" content={video.file_url} />}
                {video.file_url && <meta property="og:video:type" content="video/mp4" />}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={`${video.title} · HentaiBaza`} />
                <meta name="twitter:description" content={metaDescription} />
                {posterUrl && <meta name="twitter:image" content={posterUrl} />}
            </Helmet>

            <div className="hb-watch-container">
                {/* Top bar */}
                <TopBar
                    left={
                        <a href="/" onClick={(e) => { e.preventDefault(); navigate('/'); }} style={{ textDecoration: 'none' }}>
                            <span style={{ fontSize: 17, fontWeight: 800, color: '#e60073', letterSpacing: -0.5 }}>
                                Hentai<span style={{ color: '#fff' }}>Baza</span>
                            </span>
                        </a>
                    }
                    right={
                        <>
                            <button onClick={handleSave} style={iconBtn}>
                                <Icon name={saved ? 'bookmark-fill' : 'bookmark'} size={20} color={saved ? '#e60073' : '#E4E4E7'} />
                            </button>
                            <button style={iconBtn} onClick={handleShare}>
                                <Icon name="dots-h" size={20} color="#E4E4E7" />
                            </button>
                        </>
                    }
                />

                <div className="hb-watch-grid">
                    {/* ── Left / main column ── */}
                    <div className="hb-watch-main">
                        {/* Video player */}
                        <VideoPlayer
                            src={video.file_url}
                            poster={posterUrl}
                            title={video.title}
                            videoRef={videoRef}
                        />

                        {/* Meta */}
                        <div style={{ padding: '14px 16px 0' }}>
                            <div style={{
                                fontFamily: "'JetBrains Mono', monospace",
                                fontSize: 10, color: '#A1A1AA', letterSpacing: 1.2, marginBottom: 6,
                            }}>
                                {video.series ? `СЕРИЯ ${video.episode_number ?? 1}` : fmtDate(video.created_at)}
                            </div>

                            <h1 style={{
                                fontSize: 20, fontWeight: 700, color: '#fff',
                                letterSpacing: -0.3, lineHeight: 1.2, margin: 0,
                            }}>{video.title}</h1>

                            <div style={{
                                marginTop: 10, display: 'flex', alignItems: 'center', gap: 6,
                                fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: '#A1A1AA',
                                flexWrap: 'wrap',
                            }}>
                                <Icon name="eye" size={12} color="#A1A1AA" stroke={1.5} />
                                {fmtViews(video.views)}
                                <Dot />
                                {fmtDate(video.created_at)}
                            </div>

                            {Array.isArray(video.categories) && video.categories.length > 0 && (
                                <div style={{ marginTop: 14, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                    {video.categories.map(tag => (
                                        <TagChip
                                            key={tag}
                                            size="sm"
                                            href={`/categories/${encodeURIComponent(slugify(tag))}`}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                navigate(`/categories/${encodeURIComponent(slugify(tag))}`);
                                            }}
                                        >{tag}</TagChip>
                                    ))}
                                </div>
                            )}

                            <MetaFacts studio={video.studio} producers={video.producers} />

                            <div style={{
                                marginTop: 16,
                                display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 6,
                            }}>
                                <ActionTile
                                    icon={liked ? 'heart-fill' : 'heart'}
                                    label={fmtViews((video.likes_count || 0) + (liked ? 1 : 0))}
                                    active={liked} accent="#e60073" onClick={handleLike}
                                />
                                <ActionTile
                                    icon={saved ? 'bookmark-fill' : 'bookmark'}
                                    label="Сохранить" active={saved} onClick={handleSave}
                                />
                                <ActionTile icon="share" label="Поделиться" onClick={handleShare} />
                                <ActionTile icon="plus"  label="Коллекция"  onClick={() => navigate('/profile?tab=collections')} />
                            </div>


                            {video.description && (
                                <div style={{ marginTop: 14 }}>
                                    <p style={{
                                        fontSize: 13, color: '#D4D4D8', lineHeight: 1.55, margin: 0,
                                        display: showDesc ? 'block' : '-webkit-box',
                                        WebkitLineClamp: showDesc ? 'none' : 3,
                                        WebkitBoxOrient: 'vertical', overflow: 'hidden',
                                    }}>{video.description}</p>
                                    <button onClick={() => setShowDesc(v => !v)} style={{
                                        marginTop: 6, background: 'transparent', border: 0,
                                        color: '#A1A1AA', fontSize: 12, fontWeight: 600,
                                        cursor: 'pointer', padding: 0,
                                        fontFamily: "'Manrope', sans-serif",
                                    }}>
                                        {showDesc ? 'Свернуть' : 'Показать полностью'}
                                    </button>
                                </div>
                            )}

                            <ReactionsBlock videoId={video.id} />
                        </div>

                        {/* All episodes section */}
                        {series.length > 0 && (
                            <div style={{ marginTop: 22, padding: '0 16px' }}>
                                <div style={{ marginBottom: 14, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
                                    <div>
                                        <div style={{
                                            fontFamily: "'JetBrains Mono', monospace",
                                            fontSize: 10, color: '#e60073',
                                            letterSpacing: 1.2, textTransform: 'uppercase', marginBottom: 2,
                                        }}>Сериал</div>
                                        <div style={{ fontSize: 17, fontWeight: 800, color: '#fff', letterSpacing: -0.3 }}>
                                            Все серии · {seriesTotal}
                                        </div>
                                    </div>
                                    {series.length < seriesTotal && (
                                        <button onClick={loadAllSeries} style={{
                                            background: 'transparent',
                                            border: '1px solid #27272A',
                                            borderRadius: 8,
                                            color: '#A1A1AA',
                                            fontSize: 12, fontWeight: 600,
                                            padding: '6px 12px',
                                            cursor: 'pointer',
                                            fontFamily: "'JetBrains Mono', monospace",
                                            letterSpacing: 0.4,
                                            display: 'flex', alignItems: 'center', gap: 6,
                                            whiteSpace: 'nowrap',
                                        }}>
                                            <Icon name="grid" size={13} color="#71717A" stroke={1.5} />
                                            Показать все
                                        </button>
                                    )}
                                </div>
                                <VideoGrid videos={series} columns={2} />
                                {seriesLoading && (
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: '1fr 1fr',
                                        gap: 14,
                                        marginTop: 14,
                                    }}>
                                        {Array.from({ length: seriesTotal - series.length }).map((_, i) => (
                                            <div key={i}>
                                                <div style={{
                                                    width: '100%', aspectRatio: '2/3',
                                                    borderRadius: 10,
                                                    background: 'linear-gradient(90deg, #131316 25%, #1F1F23 50%, #131316 75%)',
                                                    backgroundSize: '200% 100%',
                                                    animation: 'hb-shimmer 1.4s ease-in-out infinite',
                                                }} />
                                                <div style={{ padding: '8px 2px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                                                    <div style={{
                                                        height: 13, borderRadius: 4, width: '85%',
                                                        background: 'linear-gradient(90deg, #131316 25%, #1F1F23 50%, #131316 75%)',
                                                        backgroundSize: '200% 100%',
                                                        animation: 'hb-shimmer 1.4s ease-in-out infinite',
                                                    }} />
                                                    <div style={{
                                                        height: 13, borderRadius: 4, width: '60%',
                                                        background: 'linear-gradient(90deg, #131316 25%, #1F1F23 50%, #131316 75%)',
                                                        backgroundSize: '200% 100%',
                                                        animation: 'hb-shimmer 1.4s ease-in-out infinite 0.1s',
                                                    }} />
                                                    <div style={{
                                                        height: 11, borderRadius: 4, width: '40%',
                                                        background: 'linear-gradient(90deg, #131316 25%, #1F1F23 50%, #131316 75%)',
                                                        backgroundSize: '200% 100%',
                                                        animation: 'hb-shimmer 1.4s ease-in-out infinite 0.2s',
                                                    }} />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Tabs */}
                        <div style={{ marginTop: 22 }}>
                            <div style={{
                                display: 'flex', gap: 4, padding: '0 16px',
                                borderBottom: '1px solid #18181B',
                            }}>
                                {tabs.map(t => (
                                    <button key={t.key} onClick={() => setActiveTab(t.key)} style={{
                                        padding: '12px 6px', background: 'transparent', border: 0,
                                        borderBottom: `2px solid ${activeTab === t.key ? '#e60073' : 'transparent'}`,
                                        color: activeTab === t.key ? '#fff' : '#71717A',
                                        fontSize: 13, fontWeight: 600, cursor: 'pointer',
                                        marginBottom: -1, whiteSpace: 'nowrap',
                                        fontFamily: "'Manrope', sans-serif",
                                    }}>{t.label}</button>
                                ))}
                            </div>

                            {activeTab === 'comments' && (
                                <div style={{ padding: '16px 16px 24px' }}>
                                    <CommentComposer videoId={video.id} onPosted={() => {
                                        setCommKey(k => k + 1);
                                        fetch(`${API}/comments.php?video_id=${id}`)
                                            .then(r => r.json()).then(setComments).catch(() => {});
                                    }} />
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                        {comments.map((c, i) => <CommentItem key={`${c.id ?? i}-${commKey}`} comment={c} />)}
                                        {comments.length === 0 && (
                                            <div style={{ color: '#71717A', fontSize: 13, textAlign: 'center', padding: '20px 0' }}>
                                                Будьте первым, кто оставит комментарий
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                        </div>

                        {/* Mobile-only: похожие видео */}
                        {related.length > 0 && (
                            <div className="hb-watch-mobile-related" style={{ marginTop: 22, padding: '0 16px 8px' }}>
                                <div style={{ marginBottom: 14 }}>
                                    <div style={{
                                        fontFamily: "'JetBrains Mono', monospace",
                                        fontSize: 10, color: '#71717A',
                                        letterSpacing: 1.2, textTransform: 'uppercase',
                                    }}>Похожие видео</div>
                                </div>
                                <VideoGrid videos={related} columns={2} />
                            </div>
                        )}
                    </div>

                    {/* ── Desktop sidebar: похожие видео ── */}
                    <div className="hb-watch-sidebar">
                        <div style={{
                            fontFamily: "'JetBrains Mono', monospace",
                            fontSize: 10, color: '#71717A',
                            letterSpacing: 1.2, textTransform: 'uppercase',
                            marginBottom: 14,
                        }}>Похожие видео</div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                            {related.map(v => <VideoCard key={v.id} video={v} />)}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
